import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"
import { API_URL } from "../constants"

const initialState = {
  creditTransactions: [],
  debitTransactions: [],
  summary: {
    totalIn: 0,
    totalOut: 0,
  },
  loading: false,
  error: null,
  filterModalOpen: false,
  filterType: "all", // Changed from "singleDay" to "all"
  startDate: new Date().toISOString().split("T")[0],
  endDate: new Date().toISOString().split("T")[0],
}

// Get all transactions by type
export const getTransactions = createAsyncThunk(
  "transactions/getTransactions",
  async ({ creditType, filterType, startDate, endDate }, { getState, rejectWithValue }) => {
    try {
      const {
        auth: { userInfo },
      } = getState()

      if (!userInfo || !userInfo.token) {
        return rejectWithValue("Authentication required")
      }

      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
        params: {
          filterType,
          startDate,
          endDate,
        },
      }

      const { data } = await axios.get(`${API_URL}/api/transactions/${creditType}`, config)

      // Also fetch summary data
      const summaryResponse = await axios.get(`${API_URL}/api/transactions/summary/${creditType}`, {
        ...config,
        params: {
          filterType,
          startDate,
          endDate,
        },
      })

      return {
        ...data,
        summary: summaryResponse.data,
      }
    } catch (error) {
      console.error("Error fetching transactions:", error.response?.data || error.message)
      return rejectWithValue(
        error.response?.data?.message || error.response?.data || error.message || "Failed to fetch transactions",
      )
    }
  },
)

const transactionSlice = createSlice({
  name: "transactions",
  initialState,
  reducers: {
    resetTransactionState: (state) => {
      state.loading = false
      state.error = null
    },
    clearTransactionData: (state) => {
      state.creditTransactions = []
      state.debitTransactions = []
      state.summary = {
        totalIn: 0,
        totalOut: 0,
      }
    },
    setFilterModalOpen: (state, action) => {
      state.filterModalOpen = action.payload
    },
    setFilterType: (state, action) => {
      state.filterType = action.payload
    },
    setStartDate: (state, action) => {
      state.startDate = action.payload
    },
    setEndDate: (state, action) => {
      state.endDate = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // Get transactions
      .addCase(getTransactions.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getTransactions.fulfilled, (state, action) => {
        state.loading = false
        state.creditTransactions = action.payload.creditTransactions || []
        state.debitTransactions = action.payload.debitTransactions || []
        state.summary = action.payload.summary || { totalIn: 0, totalOut: 0 }
      })
      .addCase(getTransactions.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export const {
  resetTransactionState,
  clearTransactionData,
  setFilterModalOpen,
  setFilterType,
  setStartDate,
  setEndDate,
} = transactionSlice.actions

export default transactionSlice.reducer
